// AdminCreateCourse: single-lesson per episode UI
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiPlus, 
  FiX, 
  FiSave,
  FiTrash2
} from 'react-icons/fi';
import axios from 'axios';
import toast from 'react-hot-toast';
import RichTextEditor from '../../components/ui/RichTextEditor';

const AdminCreateCourse = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const [courseData, setCourseData] = useState({
    title: '',
    subtitle: '',
    description: '',
    
    thumbnail: '',

    price: 0,
    originalPrice: 0,
    tags: [],
    requirements: [],
    learningOutcomes: [],
    units: []
  });

  const handleInputChange = (field, value) => {
    setCourseData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddUnit = () => {
    const newUnit = {
      title: '',
      description: '',
      order: courseData.units.length + 1,
      lessons: [
        {
          title: '',
          description: '',
          content: '',
          videoUrl: '',
          duration: 0,
          order: 1,
          resources: []
        }
      ]
    };
    setCourseData(prev => ({
      ...prev,
      units: [...prev.units, newUnit]
    }));
  };

  const handleUpdateUnit = (unitIndex, field, value) => {
    setCourseData(prev => ({
      ...prev,
      units: prev.units.map((unit, index) => 
        index === unitIndex ? { ...unit, [field]: value } : unit
      )
    }));
  };

  const handleRemoveUnit = (unitIndex) => {
    setCourseData(prev => ({
      ...prev,
      units: prev.units.filter((_, index) => index !== unitIndex)
    }));
  };

  // Each 회차 has a single lesson; no add/remove lesson handlers needed

  const handleUpdateLesson = (unitIndex, lessonIndex, field, value) => {
    setCourseData(prev => ({
      ...prev,
      units: prev.units.map((unit, uIndex) => 
        uIndex === unitIndex 
          ? {
              ...unit,
              lessons: unit.lessons.map((lesson, lIndex) => 
                lIndex === lessonIndex 
                  ? { ...lesson, [field]: value }
                  : lesson
              )
            }
          : unit
      )
    }));
  };

  // Removal of lessons is not supported in single-lesson mode

  const handleAddResource = (unitIndex, lessonIndex) => {
    const newResource = {
      title: '',
      fileUrl: '',
      fileType: '',
      fileSize: 0,
      description: ''
    };
    
    setCourseData(prev => ({
      ...prev,
      units: prev.units.map((unit, uIndex) => 
        uIndex === unitIndex 
          ? {
              ...unit,
              lessons: unit.lessons.map((lesson, lIndex) => 
                lIndex === lessonIndex 
                  ? { ...lesson, resources: [...lesson.resources, newResource] }
                  : lesson
              )
            }
          : unit
      )
    }));
  };

  const handleUpdateResource = (unitIndex, lessonIndex, resourceIndex, field, value) => {
    setCourseData(prev => ({
      ...prev,
      units: prev.units.map((unit, uIndex) => 
        uIndex === unitIndex 
          ? {
              ...unit,
              lessons: unit.lessons.map((lesson, lIndex) => 
                lIndex === lessonIndex 
                  ? {
                      ...lesson,
                      resources: lesson.resources.map((resource, rIndex) => 
                        rIndex === resourceIndex 
                          ? { ...resource, [field]: value }
                          : resource
                      )
                    }
                  : lesson
              )
            }
          : unit
      )
    }));
  };

  const handleRemoveResource = (unitIndex, lessonIndex, resourceIndex) => {
    setCourseData(prev => ({
      ...prev,
      units: prev.units.map((unit, uIndex) => 
        uIndex === unitIndex 
          ? {
              ...unit,
              lessons: unit.lessons.map((lesson, lIndex) => 
                lIndex === lessonIndex 
                  ? {
                      ...lesson,
                      resources: lesson.resources.filter((_, rIndex) => rIndex !== resourceIndex)
                    }
                  : lesson
              )
            }
          : unit
      )
    }));
  };

  const handleSubmit = async () => {
    if (!courseData.title || !courseData.description) {
      toast.error('필수 정보를 모두 입력해주세요');
      return;
    }

    if (courseData.units.length === 0) {
      toast.error('최소 하나의 회차를 추가해주세요');
      return;
    }

    for (const unit of courseData.units) {
      if (!unit.lessons || unit.lessons.length !== 1) {
        toast.error('각 회차에는 하나의 강의만 포함됩니다');
        return;
      }
    }

    setLoading(true);
    try {
      // Ensure each lesson has a title (copy from unit title if missing)
      const normalizedData = {
        ...courseData,
        price: Math.max(0, Number(courseData.price) || 0),
        originalPrice: Math.max(0, Number(courseData.originalPrice) || 0),
        units: courseData.units.map(unit => ({
          ...unit,
          lessons: unit.lessons.map(lesson => ({
            ...lesson,
            title: lesson.title || unit.title || 'Untitled Lesson'
          }))
        }))
      };
      
      await axios.post('/api/admin/courses', normalizedData);
      toast.success('강의가 성공적으로 생성되었습니다!');
      navigate('/admin/courses');
    } catch (error) {
      console.error('Error creating course:', error);
      toast.error('강의 생성에 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">기본 정보</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            강의 제목 *
          </label>
          <input
            type="text"
            value={courseData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="강의 제목을 입력하세요"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            부제목
          </label>
          <input
            type="text"
            value={courseData.subtitle}
            onChange={(e) => handleInputChange('subtitle', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="강의 부제목을 입력하세요"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          강의 설명 *
        </label>
        <RichTextEditor
          value={courseData.description}
          onChange={(value) => handleInputChange('description', value)}
          placeholder="강의에 대한 자세한 설명을 입력하세요"
        />
        <p className="text-sm text-gray-500 mt-2">
          강의 소개, 커리큘럼, 학습 목표 등을 자세히 작성해주세요. 리치 텍스트 에디터를 사용하여 글꼴, 링크, 이미지를 추가할 수 있습니다.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            판매 가격 (원) *
          </label>
          <input
            type="number"
            min="0"
            step="1000"
            value={courseData.price}
            onChange={(e) => handleInputChange('price', Math.max(0, parseInt(e.target.value, 10) || 0))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="예: 99000"
            required
          />
          <p className="text-sm text-gray-500 mt-1">무료 강의는 0원으로 입력하세요.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            할인 전 가격 (원)
          </label>
          <input
            type="number"
            min="0"
            step="1000"
            value={courseData.originalPrice}
            onChange={(e) => handleInputChange('originalPrice', Math.max(0, parseInt(e.target.value, 10) || 0))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="선택 사항"
          />
          <p className="text-sm text-gray-500 mt-1">
            판매 가격보다 클 때만 할인가로 표시됩니다.
            {courseData.originalPrice > courseData.price && courseData.price > 0 && (
              <span className="block text-primary-600 mt-1">
                할인율 약 {Math.round((1 - courseData.price / courseData.originalPrice) * 100)}%
              </span>
            )}
          </p>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">회차 구성</h3>
        <button
          type="button"
          onClick={handleAddUnit}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <FiPlus className="w-4 h-4 mr-2" />
          회차 추가
        </button>
      </div>

      {courseData.units.map((unit, unitIndex) => {
        return (
          <div key={unitIndex} className="border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-md font-semibold text-gray-900">회차 {unitIndex + 1}</h4>
              <button
                type="button"
                onClick={() => handleRemoveUnit(unitIndex)}
                className="text-red-600 hover:text-red-700"
              >
                <FiTrash2 className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">회차 제목</label>
                <input
                  type="text"
                  value={unit.title}
                  onChange={(e) => handleUpdateUnit(unitIndex, 'title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="회차 제목을 입력하세요"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">회차 설명</label>
                <input
                  type="text"
                  value={unit.description}
                  onChange={(e) => handleUpdateUnit(unitIndex, 'description', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="회차 설명을 입력하세요"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">강의 시간 (분)</label>
                    <input
                      type="number"
                      value={courseData.units[unitIndex].lessons[0].duration}
                      onChange={(e) => handleUpdateLesson(unitIndex, 0, 'duration', parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">비디오 URL</label>
                  <input
                    type="url"
                    value={courseData.units[unitIndex].lessons[0].videoUrl}
                    onChange={(e) => handleUpdateLesson(unitIndex, 0, 'videoUrl', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Google Drive 링크"
                  />
                  <p className="text-xs text-gray-500 mt-1">Google Drive: https://drive.google.com/file/d/... 형식으로 입력하세요</p>
                </div>

                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">강의 내용/노트</label>
                  <textarea
                    value={courseData.units[unitIndex].lessons[0].content}
                    onChange={(e) => handleUpdateLesson(unitIndex, 0, 'content', e.target.value)}
                    rows={3}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="강의 내용이나 노트를 입력하세요"
                  />
                </div>

                <div className="border-t border-gray-200 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <h6 className="text-xs font-medium text-gray-700">첨부 자료</h6>
                    <button
                      type="button"
                      onClick={() => handleAddResource(unitIndex, 0)}
                      className="flex items-center px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                    >
                      <FiPlus className="w-3 h-3 mr-1" />
                      자료 추가
                    </button>
                  </div>

                  {courseData.units[unitIndex].lessons[0].resources.map((resource, resourceIndex) => {
                    return (
                      <div key={resourceIndex} className="border border-gray-200 rounded p-2 mb-2">
                        <div className="flex items-center justify-between mb-2">
                          <h6 className="text-xs font-medium text-gray-700">자료 {resourceIndex + 1}</h6>
                          <button
                            type="button"
                            onClick={() => handleRemoveResource(unitIndex, 0, resourceIndex)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <FiX className="w-3 h-3" />
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">자료 제목</label>
                            <input
                              type="text"
                              value={resource.title}
                              onChange={(e) => handleUpdateResource(unitIndex, 0, resourceIndex, 'title', e.target.value)}
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="자료 제목"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">파일 URL</label>
                            <input
                              type="url"
                              value={resource.fileUrl}
                              onChange={(e) => handleUpdateResource(unitIndex, 0, resourceIndex, 'fileUrl', e.target.value)}
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Google Drive 또는 직접 링크"
                            />
                            <p className="text-xs text-gray-500 mt-1">Google Drive: https://drive.google.com/file/d/... 형식으로 입력하세요</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">파일 타입</label>
                            <select
                              value={resource.fileType}
                              onChange={(e) => handleUpdateResource(unitIndex, 0, resourceIndex, 'fileType', e.target.value)}
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                            >
                              <option value="">타입 선택</option>
                              <option value="pdf">PDF</option>
                              <option value="video">Video</option>
                              <option value="zip">ZIP</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">파일 크기 (MB)</label>
                            <input
                              type="number"
                              value={resource.fileSize}
                              onChange={(e) => handleUpdateResource(unitIndex, 0, resourceIndex, 'fileSize', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="0"
                            />
                          </div>
                        </div>

                        <div className="mt-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">설명</label>
                          <input
                            type="text"
                            value={resource.description}
                            onChange={(e) => handleUpdateResource(unitIndex, 0, resourceIndex, 'description', e.target.value)}
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="자료 설명"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">추가 정보</h3>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          썸네일 이미지 URL
        </label>
        <input
          type="url"
          value={courseData.thumbnail}
          onChange={(e) => handleInputChange('thumbnail', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="https://example.com/thumbnail.jpg"
        />
      </div>



      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          태그 (쉼표로 구분)
        </label>
        <input
          type="text"
          value={courseData.tags.join(', ')}
          onChange={(e) => handleInputChange('tags', e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="태그1, 태그2, 태그3"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          선수 지식 (한 줄씩)
        </label>
        <textarea
          value={courseData.requirements.join('\n')}
          onChange={(e) => handleInputChange('requirements', e.target.value.split('\n').filter(req => req.trim()))}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="이 강의를 수강하기 전에 필요한 지식이나 경험을 입력하세요"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          학습 목표 (한 줄씩)
        </label>
        <textarea
          value={courseData.learningOutcomes.join('\n')}
          onChange={(e) => handleInputChange('learningOutcomes', e.target.value.split('\n').filter(outcome => outcome.trim()))}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="이 강의를 통해 얻을 수 있는 지식이나 기술을 입력하세요"
        />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">새 강의 추가</h1>
          <p className="mt-2 text-gray-600">새로운 강의를 생성하고 관리하세요</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep >= step 
                      ? 'bg-primary-600 text-white' 
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {step}
                  </div>
                  {step < 3 && (
                    <div className={`w-16 h-1 mx-2 ${
                      currentStep > step ? 'bg-primary-600' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
            <div className="text-sm text-gray-600">
              {currentStep === 1 && '기본 정보'}
              {currentStep === 2 && '강의 구성'}
              {currentStep === 3 && '추가 정보'}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm p-8">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
              disabled={currentStep === 1}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              이전
            </button>

            <div className="flex items-center space-x-4">
              {currentStep < 3 ? (
                <button
                  type="button"
                  onClick={() => setCurrentStep(currentStep + 1)}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  다음
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      저장 중...
                    </>
                  ) : (
                    <>
                      <FiSave className="w-4 h-4 mr-2" />
                      강의 저장
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCreateCourse; 